import type { ReactElement } from "react";

import { ErrorPageIllustration } from "@/components/errors/ErrorPageIllustration";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const SUPPORT_EMAIL = "hello@asaferrisk.com";

export interface ErrorBoundaryFallbackProps {
  error?: Error | null;
  reset?: () => void;
}

function supportMailto(error?: Error | null): string {
  const detail = error?.message?.trim();
  const page = typeof window === "undefined" ? "" : window.location.href;
  const body = [
    "A page in A Safer Risk didn't load.",
    "",
    page ? `Page: ${page}` : "",
    detail ? `Details: ${detail}` : "",
  ]
    .filter((line) => line.length > 0)
    .join("\n");

  const params = new URLSearchParams({
    subject: "A page didn't load",
    body,
  });

  return `mailto:${SUPPORT_EMAIL}?${params.toString()}`;
}

function reloadPage(reset?: () => void): void {
  if (typeof reset === "function") {
    reset();
    return;
  }
  window.location.reload();
}

function goBack(): void {
  if (window.history.length > 1) {
    window.history.back();
    return;
  }
  window.location.assign("/");
}

export function DefaultErrorFallback({
  error,
  reset,
}: ErrorBoundaryFallbackProps): ReactElement {
  const detail = error?.message?.trim();

  return (
    <div
      className="flex w-full flex-1 items-center justify-center px-4 py-8 sm:py-12"
      role="alert"
    >
      <Card className="w-full max-w-lg shadow-sm">
        <CardHeader className="items-center space-y-4">
          <ErrorPageIllustration />
          <CardTitle className="w-full text-base font-semibold">This page didn&apos;t load</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Something interrupted this page. Reload it to pick up the latest version. That usually
            opens the page.
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
            <li>Reload the page.</li>
            <li>If it still doesn&apos;t open, go back and open it again.</li>
            <li>If it keeps happening, contact support. The message includes this page and the error.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => reloadPage(reset)}>
              Reload page
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={goBack}>
              Go back
            </Button>
            <Button type="button" size="sm" variant="ghost" asChild>
              <a href={supportMailto(error)}>Contact support</a>
            </Button>
          </div>
          {detail ? (
            <details className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <summary className="cursor-pointer text-muted-foreground">Details for support</summary>
              <p className="mt-2 break-words font-mono text-xs text-foreground">{detail}</p>
            </details>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
